import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalModal } from "./GlobalModalContext";

export default function QuickCreateDropdown() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const { openAddLead, openLogCall, openScheduleCall, openQuickNote } = useGlobalModal();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const handleNavigate = (path: string) => {
    setOpen(false);
    setTimeout(() => navigate(path), 100);
  };

  return (
    <div ref={ref} className="relative px-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center gap-2 w-full mx-auto px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-all duration-200 active:scale-[0.98] whitespace-nowrap"
        style={{ width: "calc(100% - 16px)" }}
      >
        <i className="ri-add-line text-base w-4 h-4 flex items-center justify-center" />
        Quick Create
      </button>

      {open && (
        <div className="absolute top-full left-2 right-2 mt-1.5 bg-white rounded-xl border border-gray-200 shadow-xl z-50 py-2 overflow-hidden">
          {/* CREATE section */}
          <p className="text-[10px] uppercase font-semibold text-gray-400 px-4 py-2 tracking-wider">Create</p>

          <button
            onClick={() => { setOpen(false); openAddLead(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-add-line text-indigo-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Add New Lead</p>
              <p className="text-xs text-gray-500">Capture a new sales lead</p>
            </div>
          </button>

          <button
            onClick={() => { setOpen(false); openLogCall(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-green-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-line text-green-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Log a Call</p>
              <p className="text-xs text-gray-500">Record a completed call</p>
            </div>
          </button>

          <button
            onClick={() => { setOpen(false); openScheduleCall(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-check-line text-amber-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Schedule Call</p>
              <p className="text-xs text-gray-500">Plan an upcoming call</p>
            </div>
          </button>

          <button
            onClick={() => { setOpen(false); openQuickNote(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-sticky-note-line text-purple-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Add Note</p>
              <p className="text-xs text-gray-500">Quick note on any lead</p>
            </div>
          </button>

          <button
            onClick={() => handleNavigate("/quotations/new")}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-file-list-3-line text-blue-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Create Quotation</p>
              <p className="text-xs text-gray-500">New quote for a lead</p>
            </div>
          </button>

          <button
            onClick={() => handleNavigate("/projects")}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-building-line text-indigo-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">New Project</p>
              <p className="text-xs text-gray-500">Manage properties &amp; items</p>
            </div>
          </button>

          {/* Divider */}
          <div className="my-1 border-t border-gray-100" />

          {/* NAVIGATE section */}
          <p className="text-[10px] uppercase font-semibold text-gray-400 px-4 py-2 tracking-wider">Navigate</p>

          <button
            onClick={() => handleNavigate("/leads/import")}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-upload-2-line text-gray-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Import Leads</p>
              <p className="text-xs text-gray-500">Bulk upload from CSV/Excel</p>
            </div>
          </button>

          <button
            onClick={() => handleNavigate("/campaigns/new")}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-megaphone-line text-gray-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">New Campaign</p>
              <p className="text-xs text-gray-500">Send to multiple leads</p>
            </div>
          </button>

          <button
            onClick={() => handleNavigate("/lead-capture/new")}
            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 rounded-lg mx-2 transition-colors cursor-pointer text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-global-line text-gray-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">New Web Form</p>
              <p className="text-xs text-gray-500">Capture web leads automatically</p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}